import { sortCommentsAscending } from "components/common/comments/sortComments";
import { cloneDeep } from "lodash";
import { applyPatch } from "rfc6902";
import { checkChanged } from "./checkChanged";
import {
  ADD_RECEIVED_MESSAGE,
  APPLY_ARS_UPDATES,
  CLEAR_TLF,
  CLOSE_MODAL_WINDOW,
  DISCARD_ARS_UPDATES,
  ITlfAction,
  LOAD_TLF,
  PATCH_TLF_COMMENTS,
  PROCESS_SIGNALR_NOTIFICATION,
  REDO_TLF_CHANGE,
  REMOVE_TLF_COMMENT,
  SET_DELAYED_HIERARCHY_UPDATES,
  SET_EDITOR_BLOCKS_SEARCH_PANEL,
  SET_TLF_COMMENT_PANEL,
  SET_TLF_READ_ONLY,
  SET_TLF_REQUEST_STATUS,
  SET_TLF_SUBSCRIPTION_STATUS,
  SET_VIEW_MODE,
  SHOW_MODAL_WINDOW,
  TlfContextProps,
  TOGGLE_TRAFFIC_LIGHTS,
  UNDO_TLF_CHANGE,
  UPDATE_TLF,
  UPDATE_TLF_COMMENT,
} from "./interface";
import { tlfInitialState } from "./tlfContextInitialState";

/**
 * Reducer for TLF context state transitions.
 *
 * @param state - The current `TlfContextProps` state.
 * @param action - The `ITlfAction` describing the update to apply.
 * @returns The updated `TlfContextProps` after applying the action.
 */
export const tlfContextReducer = (state: TlfContextProps, action: ITlfAction): TlfContextProps => {
  const newState = { ...state };
  switch (action.operation) {
    case ADD_RECEIVED_MESSAGE: {
      if (
        state.receivedMessages.some(
          (message) => message.messageId === action.receivedMessage.messageId,
        )
      ) {
        break;
      }
      const newMessages = [
        ...state.receivedMessages,
        {
          messageId: action.receivedMessage.messageId,
          updatedId: action.receivedMessage.groupName,
          updateTS: new Date(action.receivedMessage.timeStamp),
        },
      ];
      newState.receivedMessages = newMessages;
      break;
    }
    case APPLY_ARS_UPDATES:
      newState.applyDelayedHierarchyUpdatesTrigger = state.applyDelayedHierarchyUpdatesTrigger + 1;
      newState.delayedHierarchyUpdates = false;
      break;
    case CLEAR_TLF:
      return { ...tlfInitialState };
    case DISCARD_ARS_UPDATES:
      newState.discardDelayedHierarchyUpdatesTrigger =
        state.discardDelayedHierarchyUpdatesTrigger + 1;
      newState.delayedHierarchyUpdates = false;
      break;
    case CLOSE_MODAL_WINDOW:
      newState.showModalWindow = "";
      newState.requestStatus = { requesting: false, error: false };
      break;
    case LOAD_TLF: {
      // Reset the undo stack
      newState.ixTlfDataVersion = 0;
      newState.ixSaveVersion = 0;
      newState.tlfData = action.tlfData;
      newState.tlfDataVersions = [cloneDeep(action.tlfData)];
      newState.showModalWindow = "";
      newState.isSubscribed = action.isSubscribed;
      newState.readOnly = action.readOnly;
      newState.isTlfNew = action.isTlfNew;
      checkChanged(newState);
      break;
    }
    case PATCH_TLF_COMMENTS: {
      if (!newState.tlfData)
        throw new Error(
          `tlfContextReducer: ${action.operation}:${action.calledFrom}: TLF data not set`,
        );
      // Apply patch to the TLF comments
      const commentsPatch = action.patch;
      /* istanbul ignore else */
      if (commentsPatch && commentsPatch.length > 0) {
        // Need to clone deep as applyPatch modifies the object
        const newComments = cloneDeep(newState.tlfData.comments).sort(sortCommentsAscending);
        applyPatch(newComments, commentsPatch);
        newState.tlfData = {
          ...newState.tlfData,
          comments: newComments,
        };
        newState.forcePushToParent = true;
        checkChanged(newState);
      }
      break;
    }
    case PROCESS_SIGNALR_NOTIFICATION: {
      if (!newState.tlfData)
        throw new Error(
          `tlfContextReducer: ${action.operation}:${action.calledFrom}: TLF data not set`,
        );

      // Validate the patch before applying
      newState.tlfData = cloneDeep(newState.tlfData);
      applyPatch(newState.tlfData, action.patch);

      // Treat SignalR updates as new save points when there are no unsaved changes to avoid marking as changed
      // This prevents the save button from activating when receiving updates from other tabs
      newState.ixSaveVersion =
        state.ixSaveVersion === state.ixTlfDataVersion ? state.tlfDataVersions.length : -1;
      // Update undo stack
      newState.ixTlfDataVersion = state.tlfDataVersions.length;
      newState.tlfDataVersions = [...state.tlfDataVersions, cloneDeep(newState.tlfData)];
      newState.showModalWindow = "";
      newState.forcePushToParent = true;
      checkChanged(newState);
      break;
    }
    case REDO_TLF_CHANGE: {
      const newIx = Math.min(
        state.tlfDataVersions.length - 1,
        state.ixTlfDataVersion + (action.steps ?? 1),
      );
      if (newIx !== state.ixTlfDataVersion) {
        newState.ixTlfDataVersion = newIx;
        newState.tlfData = cloneDeep(state.tlfDataVersions[newIx]);
        checkChanged(newState);
        newState.forcePushToParent = true;
      }
      break;
    }
    case REMOVE_TLF_COMMENT: {
      if (!newState.tlfData)
        throw new Error(`tlfContextReducer: ${action.operation}: TLF data not set`);
      newState.tlfData = {
        ...newState.tlfData,
        comments: newState.tlfData.comments.filter((c) => c.id !== action.commentId),
      };
      newState.forcePushToParent = true;
      checkChanged(newState);
      break;
    }
    case SET_DELAYED_HIERARCHY_UPDATES: {
      newState.delayedHierarchyUpdates = action.delayedHierarchyUpdates;
      break;
    }
    case SET_EDITOR_BLOCKS_SEARCH_PANEL: {
      newState.editorBlocksSearchPanelStatus = action.editorBlocksSearchPanelStatus;
      break;
    }
    case SET_TLF_READ_ONLY: {
      newState.readOnly = action.readOnly;
      break;
    }
    case SET_TLF_COMMENT_PANEL: {
      newState.commentPanelStatus = action.commentPanelStatus;
      break;
    }
    case SET_TLF_REQUEST_STATUS: {
      newState.requestStatus = action.requestStatus;
      break;
    }
    case SET_TLF_SUBSCRIPTION_STATUS: {
      newState.isSubscribed = action.isSubscribed;
      break;
    }
    case SET_VIEW_MODE: {
      newState.tlfViewMode = action.tlfViewMode;
      break;
    }
    case SHOW_MODAL_WINDOW: {
      newState.showModalWindow = action.modalWindow;
      break;
    }
    case TOGGLE_TRAFFIC_LIGHTS:
      newState.showTrafficLights = action.showTrafficLights;
      break;
    case UNDO_TLF_CHANGE: {
      const newIx = Math.max(0, state.ixTlfDataVersion - (action.steps ?? 1));
      if (newIx !== state.ixTlfDataVersion) {
        newState.ixTlfDataVersion = newIx;
        newState.tlfData = cloneDeep(state.tlfDataVersions[newIx]);
        checkChanged(newState);
        newState.forcePushToParent = true;
      }
      break;
    }
    case UPDATE_TLF: {
      if (!state.tlfData) {
        throw new Error(`tlfContextReducer: ${action.operation}: TLF data not set`);
      }

      // Create new TLF data from the action
      const newTlfData = {
        ...state.tlfData,
        ...action.tlfData,
      };
      newState.tlfData = newTlfData;

      // Reset the undo stack
      if (action.reset) {
        newState.ixTlfDataVersion = 0;
        newState.ixSaveVersion = 0;
        newState.tlfDataVersions = [cloneDeep(newTlfData)];
        newState.showModalWindow = "";
        newState.forcePushToParent = true;
      }
      // Overwrite the last entry and mark is as the new save point
      else if (action.newSavePoint) {
        newState.ixSaveVersion = state.ixTlfDataVersion;
        newState.tlfDataVersions = [
          ...state.tlfDataVersions.slice(0, state.ixTlfDataVersion),
          cloneDeep(newTlfData),
        ];
        newState.showModalWindow = "";
        newState.forcePushToParent = true;
      }
      // Do not add to stack
      else if (action.doNotAddUndo) {
        newState.ixTlfDataVersion = state.ixTlfDataVersion;
        newState.tlfDataVersions = [
          ...state.tlfDataVersions.slice(0, state.ixTlfDataVersion),
          cloneDeep(newTlfData),
        ];
        newState.forcePushToParent = true;
      }
      // Just add to the stack
      else {
        // Lose save point if it is ahead in the stack
        if (state.ixSaveVersion > state.ixTlfDataVersion) {
          newState.ixSaveVersion = -1;
        }
        newState.ixTlfDataVersion = state.ixTlfDataVersion + 1;
        newState.tlfDataVersions = [
          ...state.tlfDataVersions.slice(0, newState.ixTlfDataVersion),
          cloneDeep(newTlfData),
        ];
      }
      checkChanged(newState);
      break;
    }
    case UPDATE_TLF_COMMENT: {
      if (!newState.tlfData)
        throw new Error(`tlfContextReducer: ${action.operation}: TLF data not set`);
      newState.tlfData = {
        ...newState.tlfData,
        comments: [
          ...newState.tlfData.comments.filter((c) => c.id !== action.comment.id),
          action.comment,
        ],
      };
      newState.forcePushToParent = true;
      checkChanged(newState);
      break;
    }
  }
  return newState;
};
